/**
 * The contract of `POST /api/tools/confirmOauth/:toolId` — the callback that
 * completes an external tool's OAuth authorization-code flow.
 *
 * Its own file because it is its own concern: the flow's refusal vocabulary and
 * the two redirect shapes the callback page forwards. `ztools.ts` composes it
 * into `ZTools.ConfirmOauth`.
 */

import { z } from "zod";

/**
 * Every `error.code` a refused `confirmOauth` renders. None of these is a
 * success: the route answers `{ success: true }` only when the one credential the
 * flow names was written.
 *
 * - `OAUTH_FLOW_LEGACY_STATE` — the flow predates per-flow state; start again.
 * - `OAUTH_FLOW_NOT_FOUND` — no live flow for this caller and tool. Deliberately
 *   one answer for unknown, foreign and another user's flow.
 * - `OAUTH_FLOW_EXPIRED` / `OAUTH_FLOW_ALREADY_USED` — a flow is single-use.
 * - `OAUTH_PROVIDER_DENIED` — the provider redirected back with `error`.
 * - `OAUTH_TOKEN_EXCHANGE_FAILED` — the provider refused the authorization code.
 * - `OAUTH_CREDENTIAL_NOT_PENDING` — the credential the flow was for is gone.
 * - `OAUTH_TOOL_NOT_CONFIGURED` — the tool's OAuth configuration is unusable.
 * - `OAUTH_FLOW_ACTOR_REQUIRED` — no signed-in user to own the flow.
 */
export const EXTERNAL_TOOL_OAUTH_ERROR_CODES = [
  "OAUTH_FLOW_LEGACY_STATE",
  "OAUTH_FLOW_NOT_FOUND",
  "OAUTH_FLOW_EXPIRED",
  "OAUTH_FLOW_ALREADY_USED",
  "OAUTH_PROVIDER_DENIED",
  "OAUTH_TOKEN_EXCHANGE_FAILED",
  "OAUTH_CREDENTIAL_NOT_PENDING",
  "OAUTH_TOOL_NOT_CONFIGURED",
  "OAUTH_FLOW_ACTOR_REQUIRED"
] as const;

export type ExternalToolOAuthErrorCode = (typeof EXTERNAL_TOOL_OAUTH_ERROR_CODES)[number];

/**
 * The provider redirected back with an authorization code. `state` is the
 * handshake id the authorize URL carried, forwarded verbatim.
 *
 * `scope` stays optional: RFC 6749 §4.1.2 sends it back only when the granted
 * scope is not identical to the one requested. The backend reads it when present
 * and asks whether the grant COVERS the request — a reordered or wider grant is
 * the request satisfied, and only a missing requested scope is refused.
 */
export const ConfirmOauthGrantedBodySchema = z.strictObject({
  state: z.string().min(1),
  code: z.string().min(1),
  scope: z.string().optional()
});

/**
 * The provider redirected back with `error` instead of a code (RFC 6749
 * §4.1.2.1) — the user declined, or the provider refused the request. Sent so the
 * flow is closed rather than left pending until it expires.
 */
export const ConfirmOauthDeniedBodySchema = z.strictObject({
  state: z.string().min(1),
  error: z.string().min(1),
  error_description: z.string().optional()
});

/**
 * confirmOauth body: exactly one of the two redirect shapes. Both are STRICT, so
 * a body carrying both `code` and `error` matches neither and is refused rather
 * than silently read as one of them.
 */
export const ConfirmOauthBodySchema = z.union([
  ConfirmOauthGrantedBodySchema,
  ConfirmOauthDeniedBodySchema
]);

export type ConfirmOauthBody = z.infer<typeof ConfirmOauthBodySchema>;

export const ConfirmOauthResponseDataSchema = z.object({ success: z.literal(true) });
